import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { AppDispatch, RootState } from '../../store/configureStore';
import { Cell, OrderEnv, PointOfSale, PublicOrder } from '@m4a3/api-client';
import { getCells, setSelectedCell } from '../../actions/orderActions';
import { logToDatadog } from '../../services/datadog';
import FilteredDropdown from '../../components/FilteredDropdown/FilteredDropdown';
import { getPointOfSales } from '../../actions/adminActions';
import { DateTime } from 'luxon';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';
import { API } from 'aws-amplify';
import { getShortIdForOrder } from '../../utils/orderUtils';
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import { Notification, newNotification } from '../../actions/uiActions';
import { v4 as uuidV4 } from 'uuid';
import { orderfulApi } from '../../services/api-instances';

interface CreateTestOrderProps {
  cells: Cell[];
  selectedCellId: Cell['id']
  pointOfSales: PointOfSale[];
  getCells: () => void;
  getPointofSales: (cellId: Cell['id']) => void;
  setSelectedCellId: (cellId: Cell['id']) => void;
  newNotification: (notification: Notification) => void;
}

const CreateTestOrder = ({
  cells,
  selectedCellId,
  pointOfSales,
  getCells,
  getPointofSales,
  setSelectedCellId,
  newNotification,
}: Readonly<CreateTestOrderProps>) => {
  const [createdOrder, setCreatedOrder] = useState<PublicOrder>();
  const [isCreatingOrder, setIsCreatingOrder] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [orderEircode, setOrderEircode] = useState<string>('');
  const [orderPointOfSaleId, setOrderPointOfSaleId] = useState<PointOfSale['id']>();
  const [orderExpectedDeliveryDate, setOrderExpectedDeliveryDate] = useState<PublicOrder['expectedDeliveryDate']>(DateTime.now().plus({ minutes: 30 }).toFormat(`yyyy-MM-dd'T'HH:mm`));
  const [numberOfOrders, setNumberOfOrders] = useState<number>(1);
  const [partnerName, setPartnerName] = useState<string>('MANNA');

  useEffect(() => {
    if (!cells?.length) {
      getCells();
    }
  }, []);

  useEffect(() => {
    getPointofSales(selectedCellId);
  }, [selectedCellId]);

  const getAddressInfoForEircode = async (eircode: string) => {
    return API.get('api', `/addressfind/locationId/${eircode}`, {})
      .catch(err => {
        logToDatadog('Error fetching addres info', {err, eircode}, 'error');
      });
  }

  useEffect(() => {
    setIsFormValid(checkIsFormValid())
  }, [orderEircode, orderExpectedDeliveryDate, orderPointOfSaleId, numberOfOrders]);

  const createOrder = async (): Promise<void> => {
    if (!isFormValid) {
      return;
    }
    const magicValidTwilioNumber = '+15005550006';  // this is a test number provided by twilio that is valid in any requests to their API
    
    setIsCreatingOrder(true);

    try {
      const addreddInfo = await getAddressInfoForEircode(orderEircode);
      let lastCreatedOrder;

      for (let i = 0; i < numberOfOrders; i++) {
        const order: PublicOrder = {
          status: 'CREATED',
          actualWeight: 0.0,
          orderUIDFromApp: uuidV4(),
          basket: {
            totalCost: 0.0,
            processingFee: 0.0,
            numberOfItems: 1,
            weightUnit: null,
            totalWeight: null,
            currency: 'EUR',
            items: [
              {
                unitCost: 0.0,
                unitWeight: 0.0,
                name: 'A free goodie bag',
                description: 'A free goodie bag',
                numberOfItems: 1,
                itemOptions: [],
                menuSectionItemId: null,
              },
            ],
          },
          externalProviderId: `${pointOfSales.find(({id}) => id === orderPointOfSaleId)?.fdStoreId ?? ''}`,
          externalOrderId: '',
          externalProviderName: partnerName,
          outletName: pointOfSales.find(({id}) => id === orderPointOfSaleId)?.posName,
          eircode: orderEircode,
          posId: orderPointOfSaleId,
          cellId: selectedCellId,
          partnerId: 0,
          posIdentifer: null,
          expectedDeliveryDate: new Date(orderExpectedDeliveryDate).toISOString(),  // the html datetime-local has a slightly different format to iso so converting here
          deliveryCharge: 0.0,
          metaData: '',
          dropLocationId: null,
          originalDropLocationId: null,
          customerPhoneNumber: magicValidTwilioNumber,
          duplicationReason: null,
          originalOrderId: null,
          storePreparedDate: null,
          orderEnv: OrderEnv.DEV,
          orderShortId: null,
        };

        if (addreddInfo?.dropLocation?.id) {
          order.dropLocationId = addreddInfo.dropLocation.id;
        }

        lastCreatedOrder = await orderfulApi.createOrder(order);
      }

      setCreatedOrder(lastCreatedOrder);
    } catch (error) {
      logToDatadog('Error creating test order', {error}, 'error');
      newNotification({
        variant: 'error',
        message: 'Error creating order.'
      })
    }

    setIsCreatingOrder(false);
  };

  const clearForm = () => {
    setOrderEircode('');
    setOrderPointOfSaleId(null);
    setOrderExpectedDeliveryDate(DateTime.now().plus({ minutes: 30 }).toFormat(`yyyy-MM-dd'T'HH:mm`));
    setCreatedOrder(null);
    setNumberOfOrders(1);
    setPartnerName('MANNA');
  };

  const checkIsFormValid = (): boolean => {
    return Boolean(orderEircode) && Boolean(selectedCellId) && Boolean(orderExpectedDeliveryDate) && Boolean(orderPointOfSaleId) && numberOfOrders > 0;
  }

  const normaliseAndSetOrderEircode = (eircode: string) => {
    const normalisedEircode = eircode.replace(/\s/g, '').toUpperCase();
    setOrderEircode(normalisedEircode);
  }

  const getHeader = ():JSX.Element => {
    return <header className="p-6 bg-white">
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            New test order
          </h2>
        </div>
        <div className="mt-4 flex md:ml-4 md:mt-0">
          <button
            type="button"
            onClick={clearForm}
            className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            Clear
          </button>
          <button
            type="button"
            disabled={!isFormValid}
            onClick={createOrder}
            className={`ml-3 inline-flex items-center rounded-md ${isFormValid ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-200 cursor-not-allowed hover:bg-blue-200 text-slate-500'} px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600`}
          >
            Create {isCreatingOrder ? <div className='ml-2'> <LoadingSpinner size={20} /></div> : <></>}
          </button>
        </div>
      </div>
    </header>;
  };

  return (
    <div>
      {getHeader()}

      <div className="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-6 p-6">
        <div className="sm:col-span-3">
          <div>
            <div className="mt-2">
              <FilteredDropdown
                label="Cell"
                value={selectedCellId || 0}
                onChange={(newCell: number) => {
                  setSelectedCellId(newCell)
                }}
                options={cells?.map((cell: Cell) => ({
                  value: cell.id,
                  displayText: cell.displayName,
                  subText: `${cell.cellType?.toLowerCase()} cell`
                }))} />
            </div>
          </div>

          <div>
            <div className="mt-2">
              <FilteredDropdown
                label="Point of sale"
                value={orderPointOfSaleId || 0}
                onChange={(pointOfSaleId: PointOfSale['id']) => {
                  setOrderPointOfSaleId(pointOfSaleId)
                }}
                options={pointOfSales?.map((pointOfSale: PointOfSale) => ({
                  value: pointOfSale.id,
                  displayText: pointOfSale.posName,
                }))} />
            </div>
          </div>

          <div>
            <div className="mt-2">
              <FilteredDropdown
                label="Partner"
                value={partnerName}
                onChange={(val: string | number) => setPartnerName(val as string)}
                options={[
                  { value: 'MANNA', displayText: 'Manna' },
                  { value: 'DELIVEROO', displayText: 'Deliveroo' },
                  { value: 'JUSTEAT', displayText: 'JustEat' },
                  { value: 'FLIPDISH', displayText: 'Flipdish' },
                ]}
              />
            </div>
          </div>

          <div>
            <label htmlFor="eircode" className="block text-sm font-medium leading-6 text-gray-900">
              Eircode
            </label>
            <div className="mt-2">
              <input
                type="text"
                name="eircode"
                id="eircode"
                value={orderEircode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  normaliseAndSetOrderEircode(e.target.value)
                }}
                autoComplete="postal-code"
                className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>

          <div>
            <label htmlFor="expected-delivery-date" className="block text-sm font-medium leading-6 text-gray-900">
              Expected delivery date
            </label>
            <div className="mt-2">
            <input
              type="datetime-local"
              className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
              id="expected-delivery-date"
              name="expected-delivery-date"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setOrderExpectedDeliveryDate(e.target.value)
              }}
              value={orderExpectedDeliveryDate}
              min={DateTime.now().startOf('day').toFormat(`yyyy-MM-dd'T'HH:mm`)}
              max={DateTime.now().endOf('day').toFormat(`yyyy-MM-dd'T'HH:mm`)} />
            </div>
          </div>

          <div>
            <label htmlFor="numberOfOrders" className="block text-sm font-medium leading-6 text-gray-900">
              Number of orders
            </label>
            <div className="mt-2">
              <input
                type="number"
                name="numberOfOrders"
                id="numberOfOrders"
                min={1}
                value={numberOfOrders}
                onChange={(e) => setNumberOfOrders(parseInt(e.target.value))}
                className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
        </div>
        {createdOrder ? <div className="sm:col-span-3">
          <div className="rounded-md bg-green-50 p-4 border border-1 border-green-200 mt-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Order #{getShortIdForOrder(createdOrder)} created</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>You can now view the order in Order Hub</p>
                </div>
                <div className="mt-4">
                  <div className="-mx-2 -my-1.5 flex">
                    <Link to={`/restaurant/orderhubSSE`}
                      target='_blank'
                      className="rounded-md bg-green-50 px-2 py-1.5 text-sm font-medium text-green-800 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 focus:ring-offset-green-50"
                    >
                      View in Order Hub
                    </Link>
                    <button
                      type="button"
                      onClick={clearForm}
                      className="ml-3 rounded-md bg-green-50 px-2 py-1.5 text-sm font-medium text-green-800 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 focus:ring-offset-green-50"
                    >
                      Create another
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> : <></>}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  cells: state.orders.cells,
  selectedCellId: state.orders.selectedCellId,
  pointOfSales: state.admin.pointOfSales,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getCells: () => getCells()(dispatch),
  setSelectedCellId: (cellId: Cell['id']) => setSelectedCell(cellId)(dispatch),
  getPointofSales: (cellId: Cell['id']) => getPointOfSales(cellId)(dispatch),
  newNotification: (notification: Notification) => newNotification(notification)(dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CreateTestOrder);
